import { useEffect, useState, type FormEvent } from 'react'
import type { LineMerchandise, OrderLineInput, OrderLineKind, OrderLineView, UpdateOrderLineRequest } from '@fitting-room/contracts'
import { defaultCommissionable, type LineDiscount } from '@fitting-room/contracts/order-math'
import { Search } from 'lucide-react'
import { assetUrl } from '../../../lib/api'
import { useAddOrderLine, useBackOffice, useGarmentLookup, useRackLookup, useUpdateOrderLine } from '../../../lib/adminQueries'
import { dollarsFromMinor, formatUsd, minorFromDollars } from '../../../lib/appointments'
import { percentFromBps } from '../../../lib/backOffice'
import { GROUP_ITEM_WORD, GROUP_LABEL, LINE_KIND_LABEL, discountFromInput, formatCents, kindsForGroup, previewLine } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { Chip } from '../Chip'
import { FormField, FormSheet } from '../FormSheet'

export type DiscountMode = 'none' | 'percent' | 'amount'

/** Where a line may still change its type: nothing ordered, nothing picked up (the API's `kindChangeProblem`). */
const KIND_CHANGE_FROM: readonly string[] = ['none', 'to_order', 'on_hold']

/**
 * Adding a line to an order, or correcting one — the order page's "Add more"
 * under Dresses, Accessories or Other accessories. After tperent's confirm
 * step and dress picker, cut down to a sale: how it is bought (a special
 * order or off the rack), its style number, color and size, the price, the
 * custom cost and the discount — a preset, a percentage, or an amount — with
 * the line's total worked out as it is typed, by the same arithmetic the
 * order will store.
 *
 * The style number is the line's name (`docs/22` point 7): a pick from the
 * catalog or the rack fills it in, and so does typing it. Neither pick is
 * required — an off-the-rack piece the rail never listed is written by its
 * style number alone.
 *
 * Editing never changes the kind or the rack piece: those decide what is
 * held and where the line is on its way, so they are a remove and an add.
 */
export function OrderLineDialog({
  orderId,
  line,
  merchandise = 'dress',
  open,
  onClose,
}: {
  readonly orderId: string
  /** The line to edit; absent to add one. */
  readonly line?: OrderLineView | undefined
  /** The group a new line goes in. */
  readonly merchandise?: LineMerchandise
  readonly open: boolean
  readonly onClose: () => void
}) {
  const group = line?.merchandise ?? merchandise
  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title={line ? 'Edit line' : `Add to ${GROUP_LABEL[group]}`}
      description={line ? 'A change to a line is kept on the order’s history with its reason.' : undefined}
    >
      {open ? <LineForm key={line?.id ?? `new-${group}`} orderId={orderId} line={line} group={group} onClose={onClose} /> : null}
    </FormSheet>
  )
}

function LineForm({
  orderId,
  line,
  group,
  onClose,
}: {
  readonly orderId: string
  readonly line?: OrderLineView | undefined
  readonly group: LineMerchandise
  readonly onClose: () => void
}) {
  const { token } = useStaffToken()
  const settings = useBackOffice(token).data
  const add = useAddOrderLine(token, orderId)
  const edit = useUpdateOrderLine(token, orderId, line?.id ?? '')
  const save = line ? edit : add
  const editing = line !== undefined
  // A written line may still change how it is bought until something has
  // happened on its way — the API's rule (`kindChangeProblem`), drawn here so
  // the select is never offered when the answer would be no.
  const kindLocked = editing && (Boolean(line.rackItemId) || !KIND_CHANGE_FROM.includes(line.fulfillment))
  const groupKinds = kindsForGroup(group)
  const kinds = !editing ? groupKinds : kindLocked ? [line.kind] : groupKinds.includes(line.kind) ? groupKinds : [line.kind, ...groupKinds]

  const [kind, setKind] = useState<OrderLineKind>(line?.kind ?? kinds[0] ?? 'made_to_order')
  const [garmentId, setGarmentId] = useState<string | null>(line?.garmentId ?? null)
  const [rackItemId, setRackItemId] = useState<string | null>(line?.rackItemId ?? null)
  const [description, setDescription] = useState(line?.description ?? '')
  const [styleCode, setStyleCode] = useState(line?.styleCode ?? '')
  const [size, setSize] = useState(line?.size ?? '')
  const [color, setColor] = useState(line?.color ?? '')
  const [quantity, setQuantity] = useState(String(line?.quantity ?? 1))
  const [price, setPrice] = useState(line ? dollarsFromMinor(line.unitPriceMinor) : '')
  const [customCost, setCustomCost] = useState(line && line.customCostMinor > 0 ? dollarsFromMinor(line.customCostMinor) : '')
  const [discountMode, setDiscountMode] = useState<DiscountMode>(line?.discountKind ?? 'none')
  const [discountText, setDiscountText] = useState(
    line?.discountKind === 'percent' ? percentFromBps(line.discountValue) : line?.discountKind === 'amount' ? dollarsFromMinor(line.discountValue) : '',
  )
  const [taxable, setTaxable] = useState(line?.taxable ?? true)
  const [commissionable, setCommissionable] = useState<boolean | null>(line?.commissionable ?? null)
  const [accessorySource, setAccessorySource] = useState<'stock' | 'order'>('stock')
  const [takenToday, setTakenToday] = useState(false)
  const [pickingRack, setPickingRack] = useState(false)
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // The older kinds of the `other` group are described, not styled: "Hem and bustle".
  const described = group === 'other'
  const earnsCommission = commissionable ?? defaultCommissionable(kind)
  const canTakeToday = !editing && (kind === 'off_the_rack' || (kind === 'accessory' && accessorySource === 'stock'))
  const onePiece = kind === 'off_the_rack' && Boolean(rackItemId)
  const qty = onePiece ? 1 : Number(quantity)
  const unitMinor = minorFromDollars(price)
  const customMinor = minorFromDollars(customCost)
  const discount: LineDiscount | null | undefined = discountMode === 'none' ? null : discountFromInput(discountMode, discountText)
  const maxPct = settings?.maxDiscountPct ?? 100
  const preview =
    Number.isInteger(qty) && qty > 0 && typeof unitMinor === 'number' && discount !== undefined && customMinor !== undefined
      ? previewLine(qty, unitMinor, discount, maxPct, customMinor ?? 0)
      : null

  const changeKind = (next: OrderLineKind) => {
    setKind(next)
    // What was picked belongs to the kind it was picked for.
    if (next !== 'made_to_order') setGarmentId(null)
    if (next !== 'off_the_rack') {
      setRackItemId(null)
      setPickingRack(false)
    }
    setTakenToday(false)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next: Record<string, string> = {}
    if (described && !description.trim()) next.description = 'Say what it is, like “Hem and bustle”.'
    if (!Number.isInteger(qty) || qty < 1 || qty > 99) next.quantity = 'A whole number from 1 to 99.'
    if (unitMinor === undefined || unitMinor === null) next.price = 'A price in dollars, like 2,400.'
    if (customMinor === undefined) next.customCost = 'An amount in dollars, like 215.'
    if (discount === undefined) next.discount = discountMode === 'percent' ? 'A percentage, like 12.5.' : 'An amount in dollars, like 150.'
    if (preview?.overCap) next.discount = preview.overCap
    if (editing && !reason.trim()) next.reason = 'Say why, for the order’s history.'
    setErrors(next)
    if (Object.keys(next).length > 0) return

    const style = styleCode.trim()
    // The style number is the line's name; a line with none is named for its group.
    const name = described ? description.trim() : style || (editing && line.description) || GROUP_ITEM_WORD[group]
    const common = {
      description: name,
      quantity: qty,
      unitPriceMinor: unitMinor as number,
      customCostMinor: customMinor ?? 0,
      discount: discount ?? null,
      taxable,
      commissionable: earnsCommission,
    }
    if (editing) {
      const body: UpdateOrderLineRequest = {
        ...common,
        styleCode: style || null,
        garmentId: kind === 'made_to_order' ? garmentId : null,
        size: size.trim() || null,
        color: color.trim() || null,
        ...(kind !== line.kind ? { kind } : {}),
        reason: reason.trim(),
      }
      edit.mutate(body, { onSuccess: onClose })
      return
    }
    const body: OrderLineInput = {
      ...common,
      kind,
      merchandise: group,
      ...(style ? { styleCode: style } : {}),
      ...(garmentId && kind === 'made_to_order' ? { garmentId } : {}),
      ...(rackItemId && kind === 'off_the_rack' ? { rackItemId } : {}),
      ...(size.trim() ? { size: size.trim() } : {}),
      ...(color.trim() ? { color: color.trim() } : {}),
      ...(kind === 'accessory' ? { accessorySource } : {}),
      ...(canTakeToday && takenToday ? { takenToday: true } : {}),
    }
    add.mutate(body, { onSuccess: onClose })
  }

  return (
    <form onSubmit={submit} noValidate>
      <FormField label="Type">
        <select className="st-select" value={kind} disabled={kinds.length < 2} onChange={(event) => changeKind(event.target.value as OrderLineKind)}>
          {kinds.map((value) => (
            <option key={value} value={value}>
              {typeLabel(value)}
            </option>
          ))}
        </select>
      </FormField>
      {editing && kind === 'accessory' && !kindLocked ? (
        <p className="st-meta -mt-2 mb-3 text-xs">Nobody said whether this was a special order or off the rack. Pick one.</p>
      ) : null}
      {editing && kindLocked && kinds.length < 2 && group !== 'other' ? (
        <p className="st-meta -mt-2 mb-3 text-xs">
          {line.rackItemId ? 'It holds a piece from the rack' : 'It’s already on its way'}, so its type stays. To change it, take it off
          and add it again.
        </p>
      ) : null}

      {kind === 'made_to_order' && group !== 'shoes' ? (
        <GarmentPicker
          selected={garmentId}
          onPick={(garment) => {
            setGarmentId(garment.id)
            // Titled by style number, as the shop names a gown; the name only for a style without one.
            setStyleCode(garment.code ?? garment.name)
            setDescription(garment.code ?? garment.name)
            if (garment.priceMinor !== undefined && !editing) setPrice(dollarsFromMinor(garment.priceMinor))
          }}
        />
      ) : null}
      {kind === 'off_the_rack' && !editing ? (
        pickingRack || rackItemId ? (
          <RackPicker
            selected={rackItemId}
            error={undefined}
            onPick={(item) => {
              setRackItemId(item.id)
              setStyleCode(item.styleCode ?? item.name)
              setDescription(item.styleCode ?? item.name)
              setSize(item.size)
              setColor(item.color ?? '')
              if (item.priceMinor !== undefined) setPrice(dollarsFromMinor(item.priceMinor))
            }}
          />
        ) : (
          <button type="button" className="st-btn-quiet -ml-2 mb-2" onClick={() => setPickingRack(true)}>
            <Search size={13} aria-hidden />
            Link a piece on the rack (optional)
          </button>
        )
      ) : null}
      {onePiece && !editing ? (
        <p className="st-meta -mt-1 mb-3 text-xs">
          Linked to a piece on the rack; it&rsquo;s held for this order.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              setRackItemId(null)
              setPickingRack(false)
            }}
          >
            Unlink
          </button>
        </p>
      ) : null}

      {described ? (
        <FormField label="Description" error={errors.description}>
          <input className="st-input" value={description} onChange={(event) => setDescription(event.target.value)} autoComplete="off" />
        </FormField>
      ) : null}
      <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-3">
        <FormField label={group === 'shoes' ? 'Shoe style' : 'Style #'}>
          <input className="st-input" value={styleCode} onChange={(event) => setStyleCode(event.target.value)} autoComplete="off" />
        </FormField>
        <FormField label="Color">
          <input className="st-input" value={color} onChange={(event) => setColor(event.target.value)} autoComplete="off" />
        </FormField>
        <FormField label="Size">
          <input className="st-input" value={size} onChange={(event) => setSize(event.target.value)} autoComplete="off" disabled={onePiece} />
        </FormField>
      </div>
      <div className="grid grid-cols-3 gap-x-3">
        <FormField label="Price" error={errors.price}>
          <input className="st-input" inputMode="decimal" placeholder="$" value={price} onChange={(event) => setPrice(event.target.value)} />
        </FormField>
        <FormField label="Custom cost" error={errors.customCost}>
          <input className="st-input" inputMode="decimal" placeholder="$0" value={customCost} onChange={(event) => setCustomCost(event.target.value)} />
        </FormField>
        <FormField label="Quantity" error={errors.quantity}>
          <input className="st-input" inputMode="numeric" value={onePiece ? '1' : quantity} disabled={onePiece} onChange={(event) => setQuantity(event.target.value)} />
        </FormField>
      </div>

      {kind === 'accessory' && !editing ? (
        <fieldset className="mb-3">
          <legend className="st-eyebrow mb-1.5">Where it comes from</legend>
          <div className="flex flex-wrap gap-1.5">
            <Chip pressed={accessorySource === 'stock'} onClick={() => setAccessorySource('stock')}>
              From stock
            </Chip>
            <Chip
              pressed={accessorySource === 'order'}
              onClick={() => {
                setAccessorySource('order')
                setTakenToday(false)
              }}
            >
              Ordered in
            </Chip>
          </div>
        </fieldset>
      ) : null}

      <DiscountField
        mode={discountMode}
        text={discountText}
        presets={settings?.discountPresetsPct ?? []}
        maxPct={settings?.maxDiscountPct}
        error={errors.discount ?? preview?.overCap ?? undefined}
        onChange={(mode, text) => {
          setDiscountMode(mode)
          setDiscountText(text)
        }}
      />

      <div className="mb-3 flex flex-col gap-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={taxable} onChange={(event) => setTaxable(event.target.checked)} />
          Taxable
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={earnsCommission} onChange={(event) => setCommissionable(event.target.checked)} />
          Earns commission
        </label>
        {canTakeToday ? (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={takenToday} onChange={(event) => setTakenToday(event.target.checked)} />
            The customer is taking it home today
          </label>
        ) : null}
      </div>

      <div className="st-card mb-3 px-3 py-2.5 text-sm" aria-live="polite">
        <PreviewRow label="Price" value={preview ? formatCents(preview.grossMinor) : '—'} />
        <PreviewRow label="Custom cost" value={customMinor ? `+ ${formatCents(customMinor)}` : '—'} />
        <PreviewRow label="Discount" value={preview && preview.discountMinor > 0 ? `− ${formatCents(preview.discountMinor)}` : '—'} />
        <PreviewRow label="Total" value={preview ? formatCents(preview.netMinor) : '—'} strong />
        <p className="st-meta mt-1 text-xs">Before tax. Tax is added once, on the order. The discount comes off the price, not the custom cost.</p>
      </div>

      {editing ? (
        <FormField label="Reason for the change" error={errors.reason}>
          <input className="st-input" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Like: owner approved 15%" />
        </FormField>
      ) : null}

      {save.isError ? (
        <p className="st-alert mb-3" role="alert">
          {save.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex gap-2">
        <button type="button" className="st-btn-ghost flex-1" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="st-btn flex-[2]" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : editing ? 'Save line' : 'Add to order'}
        </button>
      </div>
    </form>
  )
}

/**
 * The type as the select shows it. `accessory` is how accessories were
 * written before an accessory was a special order or off the rack — and how
 * the ClickUp import still writes one, since ClickUp never said — so it reads
 * as not said rather than as a third way to buy.
 */
function typeLabel(kind: OrderLineKind): string {
  return kind === 'accessory' ? '— (not specified)' : LINE_KIND_LABEL[kind]
}

function PreviewRow({ label, value, strong }: { readonly label: string; readonly value: string; readonly strong?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="st-meta">{label}</span>
      <span className={`tabular-nums ${strong ? 'font-medium' : ''}`}>{value}</span>
    </div>
  )
}

/**
 * The discount: none, one of the owner's presets, any other percentage, or a
 * dollar amount. The presets are chips because they are what is used nine
 * times in ten; the field is there for the tenth.
 */
export function DiscountField({
  mode,
  text,
  presets,
  maxPct,
  error,
  onChange,
}: {
  readonly mode: DiscountMode
  readonly text: string
  readonly presets: readonly number[]
  readonly maxPct: number | undefined
  readonly error: string | undefined
  readonly onChange: (mode: DiscountMode, text: string) => void
}) {
  const isPreset = (pct: number) => mode === 'percent' && Number(text) === pct
  const custom = mode !== 'none' && !presets.some(isPreset)
  return (
    <fieldset className="mb-3">
      <legend className="st-eyebrow mb-1.5">Discount{maxPct !== undefined ? ` · up to ${maxPct}%` : ''}</legend>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip pressed={mode === 'none'} onClick={() => onChange('none', '')}>
          None
        </Chip>
        {presets.map((pct) => (
          <Chip key={pct} pressed={isPreset(pct)} onClick={() => onChange('percent', String(pct))}>
            {pct}%
          </Chip>
        ))}
        <Chip pressed={custom && mode === 'percent'} onClick={() => onChange('percent', custom && mode === 'percent' ? text : '')}>
          Other %
        </Chip>
        <Chip pressed={mode === 'amount'} onClick={() => onChange('amount', mode === 'amount' ? text : '')}>
          $ amount
        </Chip>
      </div>
      {custom ? (
        <input
          className="st-input mt-2 w-40"
          inputMode="decimal"
          aria-label={mode === 'percent' ? 'Discount percentage' : 'Discount in dollars'}
          placeholder={mode === 'percent' ? '%' : '$'}
          value={text}
          autoFocus
          onChange={(event) => onChange(mode, event.target.value)}
        />
      ) : null}
      {error ? (
        <p className="mt-1 text-xs" style={{ color: '#8d3f2c' }} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}

/** What a stylist types into a picker, settled for a beat before it is a request. */
function useSettled(value: string, ms = 250): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

export function GarmentPicker({
  selected,
  onPick,
}: {
  readonly selected: string | null
  readonly onPick: (garment: { id: string; name: string; code?: string | undefined; priceMinor?: number | undefined }) => void
}) {
  const { token } = useStaffToken()
  const [q, setQ] = useState('')
  const settled = useSettled(q.trim())
  const lookup = useGarmentLookup(token, settled, settled.length > 0)
  const garments = settled ? (lookup.data?.garments ?? []) : []
  return (
    <div className="mb-3">
      <PickerSearch value={q} onChange={setQ} label="Find the style in the catalog" placeholder="Style number or name" />
      {settled && lookup.isSuccess && garments.length === 0 ? (
        <p className="st-meta mt-1.5 text-xs">Nothing in the catalog matches. You can still type the line in below.</p>
      ) : null}
      {garments.length > 0 ? (
        <ul className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
          {garments.map((garment) => (
            <li key={garment.id}>
              <button type="button" className="st-pick" aria-pressed={selected === garment.id} onClick={() => onPick(garment)}>
                <Thumb url={garment.imageUrl} />
                <span className="min-w-0 flex-1">
                  {/* By style number, as the shop names a gown; the name beneath when it says something more. */}
                  <span className="block truncate text-sm font-medium">{garment.code ?? garment.name}</span>
                  <span className="st-meta block truncate text-xs">
                    {[
                      garment.code ? (garment.name !== garment.code ? garment.name : null) : 'No style number',
                      garment.status !== 'active' ? 'discontinued' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {garment.priceMinor !== undefined ? <span className="text-sm tabular-nums">{formatUsd(garment.priceMinor)}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

const RACK_STATUS_WORD = { available: 'Available', held: 'Held', sold: 'Sold', withdrawn: 'Withdrawn' } as const

export function RackPicker({
  selected,
  error,
  onPick,
}: {
  readonly selected: string | null
  readonly error: string | undefined
  readonly onPick: (item: {
    id: string
    name: string
    styleCode?: string | undefined
    size: string
    color?: string | undefined
    priceMinor?: number | undefined
  }) => void
}) {
  const { token } = useStaffToken()
  const [q, setQ] = useState('')
  const settled = useSettled(q.trim())
  const lookup = useRackLookup(token, settled, true)
  const items = lookup.data?.items ?? []
  return (
    <div className="mb-3">
      <PickerSearch value={q} onChange={setQ} label="Find the piece on the rack" placeholder="Barcode, style number or size" />
      {error ? (
        <p className="mt-1 text-xs" style={{ color: '#8d3f2c' }} role="alert">
          {error}
        </p>
      ) : null}
      {lookup.isSuccess && items.length === 0 ? (
        <p className="st-meta mt-1.5 text-xs">{settled ? 'No piece on the rack matches.' : 'Nothing on the rack is available.'}</p>
      ) : null}
      {items.length > 0 ? (
        <ul className="mt-2 max-h-72 space-y-1.5 overflow-y-auto">
          {items.map((item) => {
            const free = item.status === 'available' && !item.heldBy
            return (
              <li key={item.id}>
                <button type="button" className="st-pick" aria-pressed={selected === item.id} disabled={!free} onClick={() => onPick(item)}>
                  <Thumb url={item.imageUrl} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {item.styleCode ?? item.name} · size {item.size}
                    </span>
                    <span className="st-meta block truncate text-xs">
                      {item.barcode}
                      {item.color ? ` · ${item.color}` : ''}
                      {free ? '' : ` · ${item.heldBy ? `on ${item.heldBy.orderNo}` : RACK_STATUS_WORD[item.status]}`}
                    </span>
                  </span>
                  {item.priceMinor !== undefined ? <span className="text-sm tabular-nums">{formatUsd(item.priceMinor)}</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

function PickerSearch({
  value,
  onChange,
  label,
  placeholder,
}: {
  readonly value: string
  readonly onChange: (q: string) => void
  readonly label: string
  readonly placeholder: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="st-eyebrow">{label}</span>
      <span className="relative block">
        <Search size={14} className="st-meta pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
        <input
          className="st-input st-input-search w-full pl-8"
          type="search"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
    </label>
  )
}

/** A photo if there is one; the swatch's own ground if not — never the browser's broken-image icon. */
function Thumb({ url }: { readonly url: string | undefined }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) return <span className="st-thumb" aria-hidden />
  return <img className="st-thumb" src={assetUrl(url)} alt="" loading="lazy" onError={() => setFailed(true)} />
}
